import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { FaRedo } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./TournamentDetail.css";

/* =========================
   Layout Helpers
   ========================= */
function computeLayout(bracket, idPrefix = "") {
  if (!bracket?.rounds) {
    return { roundLayouts: [], lines: [], minX: 0, maxX: 0, minY: 0, maxY: 0, matchWidth: 0, matchHeight: 0 };
  }
  const rounds = bracket.rounds;
  const matchWidth = 248;
  const matchHeight = 88;
  const xGap = 420;
  const yGapRound0 = 60;
  const yGapDefault = 72;

  const layout = rounds.map(() => []);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

  if (rounds[0]) {
    const r0 = rounds[0];
    const totalHeight = r0.length * matchHeight + (r0.length - 1) * yGapRound0;
    let yPos = -(totalHeight / 2);
    for (let i = 0; i < r0.length; i++) {
      layout[0][i] = { x: 0, y: yPos };
      yPos += matchHeight + yGapRound0;
      minX = Math.min(minX, 0);
      maxX = Math.max(maxX, matchWidth);
      minY = Math.min(minY, layout[0][i].y);
      maxY = Math.max(maxY, layout[0][i].y + matchHeight);
    }
  }

  for (let r = 1; r < rounds.length; r++) {
    // Drop round: same match count as previous round (LB 1-to-1 mapping).
    // Consolidation/merge round: fewer matches (standard 2-to-1 binary tree).
    const isDropRound = rounds[r].length === rounds[r - 1].length;

    for (let mIndex = 0; mIndex < rounds[r].length; mIndex++) {
      let centerY;
      const x = r * xGap;

      if (isDropRound) {
        // 1-to-1: each match pairs with the same-index parent
        const parent = layout[r - 1][mIndex];
        if (parent) {
          centerY = parent.y;
        } else {
          const prev = layout[r][mIndex - 1];
          centerY = prev ? prev.y + matchHeight + yGapDefault : 0;
        }
      } else {
        // 2-to-1: standard binary tree — midpoint of two parents
        const p1 = layout[r - 1][2 * mIndex];
        const p2 = layout[r - 1][2 * mIndex + 1];
        const parents = [];
        if (p1) parents.push(p1.y + matchHeight / 2);
        if (p2) parents.push(p2.y + matchHeight / 2);
        if (parents.length === 0) {
          const prev = layout[r][mIndex - 1];
          centerY = prev ? prev.y + matchHeight + yGapDefault : 0;
        } else {
          centerY = parents.reduce((a, v) => a + v, 0) / parents.length - matchHeight / 2;
        }
      }

      layout[r][mIndex] = { x, y: centerY };
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + matchWidth);
      minY = Math.min(minY, centerY);
      maxY = Math.max(maxY, centerY + matchHeight);
    }
  }

  const roundLayouts = rounds.map((rnd, r) =>
    rnd.map((match, mIndex) => {
      const pos = layout[r][mIndex];
      if (!match || !pos || typeof match !== "object") return null;

      // Hide bye matches (Player vs No Opponent) so they start in next round
      if (match.player1 === "(Bye)" || match.player2 === "(Bye)" || match.player1 === "No Opponent" || match.player2 === "No Opponent") return null;

      return {
        id: `${idPrefix}R${r}-M${mIndex}`,
        roundIndex: r,
        matchIndex: mIndex,
        player1: (match.player1 === "(TBD)" ? "TBD" : match.player1) || "TBD",
        player2: (match.player2 === "(TBD)" ? "TBD" : match.player2) || "TBD",
        score1: match.score1 ?? 0,
        score2: match.score2 ?? 0,
        x: pos.x,
        y: pos.y
      };
    })
  );

  const lines = [];
  for (let r = 1; r < rounds.length; r++) {
    const isDropRound = rounds[r].length === rounds[r - 1].length;

    for (let mIndex = 0; mIndex < rounds[r].length; mIndex++) {
      const child = layout[r][mIndex];
      const cx = child.x + matchWidth / 2;
      const cy = child.y + matchHeight / 2;

      const isBye = (m) => m && (m.player1 === "(Bye)" || m.player2 === "(Bye)" || m.player1 === "No Opponent" || m.player2 === "No Opponent");

      if (isDropRound) {
        // 1-to-1: single parent at same index
        const p = layout[r - 1][mIndex];
        const m = rounds[r - 1][mIndex];
        if (p && !isBye(m)) lines.push({ x1: p.x + matchWidth / 2, y1: p.y + matchHeight / 2, x2: cx, y2: cy });
      } else {
        // 2-to-1: two parents
        const p1 = layout[r - 1][2 * mIndex];
        const p2 = layout[r - 1][2 * mIndex + 1];
        const m1 = rounds[r - 1][2 * mIndex];
        const m2 = rounds[r - 1][2 * mIndex + 1];
        if (p1 && !isBye(m1)) lines.push({ x1: p1.x + matchWidth / 2, y1: p1.y + matchHeight / 2, x2: cx, y2: cy });
        if (p2 && !isBye(m2)) lines.push({ x1: p2.x + matchWidth / 2, y1: p2.y + matchHeight / 2, x2: cx, y2: cy });
      }
    }
  }

  return { roundLayouts, lines, minX, maxX, minY, maxY, matchWidth, matchHeight };
}

/* =========================
   Standings helpers
   ========================= */
function computeStandings(tournament) {
  const stats = {};
  const processMatch = (match) => {
    if (!match) return;
    const { player1: p1, player2: p2, score1, score2 } = match;
    if (!p1 || !p2 || p1 === "(TBD)" || p2 === "(TBD)" || p1 === "TBD" || p2 === "TBD") return;

    // Do not count byes as wins in standings
    if (p1 === "(Bye)" || p2 === "(Bye)" || p1 === "No Opponent" || p2 === "No Opponent") return;

    const s1 = score1 || 0, s2 = score2 || 0;
    if (s1 === 0 && s2 === 0) return;
    if (!stats[p1]) stats[p1] = { wins: 0, losses: 0 };
    if (!stats[p2]) stats[p2] = { wins: 0, losses: 0 };
    if (s1 > s2) { stats[p1].wins++; stats[p2].losses++; }
    else if (s2 > s1) { stats[p2].wins++; stats[p1].losses++; }
  };
  const b = tournament.bracket;
  (b.rounds || []).forEach((round) => round.forEach(processMatch));
  return Object.entries(stats)
    .map(([name, s]) => ({ name, ...s }))
    .sort((a, b) => b.wins - a.wins || a.losses - b.losses);
}

const MEDALS = ["🥇", "🥈", "🥉"];
const MEDAL_CLASSES = ["gold", "silver", "bronze"];

function isMatchIdLocked(matchId, lockedRounds) {
  if (!matchId || !lockedRounds?.length) return false;
  const key = `single_${parseInt(matchId.split("-")[0].slice(1), 10)}`;
  return lockedRounds.includes(key);
}

/* =========================
   Bye Resolution Helper
   ========================= */
function resolveAllByes(bracket, type, propagateScores = true) {
  let modified = false;

  const rounds = bracket.rounds;
  if (!rounds) return modified;

  rounds.forEach((round, rIndex) => {
    round.forEach((m, mIndex) => {
      if (!m) return;
      let winner = null;

      const isBye1 = m.player2 === "(Bye)" || m.player2 === "No Opponent";
      const isBye2 = m.player1 === "(Bye)" || m.player1 === "No Opponent";
      const isTBD1 = m.player1 === "(TBD)" || m.player1 === "TBD";
      const isTBD2 = m.player2 === "(TBD)" || m.player2 === "TBD";

      if (isBye1 && m.player1 && !isTBD1 && !isBye2) {
        if (m.score1 !== 1 || m.score2 !== 0) { m.score1 = 1; m.score2 = 0; modified = true; }
        winner = m.player1;
      } else if (isBye2 && m.player2 && !isTBD2 && !isBye1) {
        if (m.score2 !== 1 || m.score1 !== 0) { m.score2 = 1; m.score1 = 0; modified = true; }
        winner = m.player2;
      }

      if (!winner && propagateScores && (m.score1 > 0 || m.score2 > 0)) {
        if (m.score1 > m.score2) winner = m.player1;
        else if (m.score2 > m.score1) winner = m.player2;
      }

      if (winner && rounds[rIndex + 1]) {
        const nextMatchIndex = Math.floor(mIndex / 2);
        const targetSlot = (mIndex % 2 === 0) ? "player1" : "player2";
        const nextMatch = rounds[rIndex + 1][nextMatchIndex];
        if (nextMatch && nextMatch[targetSlot] !== winner) {
          nextMatch[targetSlot] = winner;
          modified = true;
        }
      }
    });
  });

  return modified;
}

/* ================
   Main Component
   ================ */
function TournamentDetail({ user }) {
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [singleLayout, setSingleLayout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showFullStandings, setShowFullStandings] = useState(false);
  const [modalMatchLocked, setModalMatchLocked] = useState(false);

  const canEditScores = !!(user && (user.role === "admin" || user.role === "super_admin" || user.role === "staff"));
  // Staff can never edit player names — only full admins can
  const canEditNamesGlobal = !!(user && (user.role === "admin" || user.role === "super_admin"));
  const canLockRounds = canEditNamesGlobal;
  const canUnlockRounds = !!(user && user.role === "super_admin");

  // Silent background refresh — no loading state, no spinner
  const refreshSilently = useCallback(async () => {
    try {
      const res = await axios.get(`/api/tournaments/${id}`);
      setTournament(res.data);
    } catch (err) {
      console.error("Silent refresh failed:", err);
    }
  }, [id]);

  const ensureByesResolved = useCallback(async (currentTournament) => {
    if (!currentTournament || !user?.token) return;
    const bracket = JSON.parse(JSON.stringify(currentTournament.bracket));
    const modified = resolveAllByes(bracket, currentTournament.type, true);

    if (modified) {
      // Update local state immediately to reflect changes
      setTournament(prev => ({ ...prev, bracket }));
      
      // Persist to server
      await axios.put(`/api/tournaments/${currentTournament._id}`, { bracket }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
    }
  }, [user]);

  // Auto-resolve byes whenever tournament data loads or changes
  useEffect(() => {
    if (tournament && user?.token) {
      ensureByesResolved(tournament);
    }
  }, [tournament, user, ensureByesResolved]);

  const lockRound = useCallback(async (bracketKey, roundIndex) => {
    if (!tournament || !user?.token) return;
    try {
      await ensureByesResolved(tournament);
      const res = await axios.put(
        `/api/tournaments/${tournament._id}/lock-round`,
        { bracketKey, roundIndex },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setTournament(res.data.tournament);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to lock round");
    }
  }, [tournament, user, ensureByesResolved]);

  const unlockRound = useCallback(async (bracketKey, roundIndex) => {
    if (!tournament || !user?.token) return;
    try {
      const res = await axios.put(
        `/api/tournaments/${tournament._id}/unlock-round`,
        { bracketKey, roundIndex },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      const unlockedTournament = res.data.tournament;

      // Optimistically update UI to avoid flicker of 0-0 scores for byes
      const optimistic = JSON.parse(JSON.stringify(unlockedTournament));
      resolveAllByes(optimistic.bracket, optimistic.type, true);
      setTournament(optimistic);

      // Ensure server has these wins recorded (pass original response which has 0-0 so it triggers update)
      await ensureByesResolved(unlockedTournament);
      refreshSilently();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to unlock round");
    }
  }, [tournament, user, ensureByesResolved]);

  // Modal state
  const [modalMatch, setModalMatch] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [closingModal, setClosingModal] = useState(false);
  const [editData, setEditData] = useState({ player1: "", score1: "", player2: "", score2: "" });
  const modalOverlayRef = useRef(null);
  const modalOpenedAtRef = useRef(0);

  // ── Recompute layout whenever tournament data changes (no loading flash) ──
  useEffect(() => {
    if (!tournament) return;
    setSingleLayout(computeLayout(tournament.bracket));
  }, [tournament]);

  // Initial load (shows spinner)
  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true);
        const res = await axios.get(`/api/tournaments/${id}`);
        setTournament(res.data);
      } catch (err) {
        console.error("Error fetching tournament:", err);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [id]);

  function closeModal() {
    setClosingModal(true);
    setTimeout(() => {
      setModalVisible(false);
      setModalMatch(null);
      setClosingModal(false);
    }, 220);
  }

  function handleOverlayClick(e) {
    if (e.target === modalOverlayRef.current) {
      if (Date.now() - modalOpenedAtRef.current < 220) return;
      closeModal();
    }
  }

  function handleMatchClick(m) {
    setModalMatch(m);
    setModalMatchLocked(isMatchIdLocked(m.id, tournament?.lockedRounds));
    setEditData({
      player1: m.player1,
      score1: m.score1 === 0 ? "" : String(m.score1),
      player2: m.player2,
      score2: m.score2 === 0 ? "" : String(m.score2)
    });
    setClosingModal(false);
    setModalVisible(true);
    modalOpenedAtRef.current = Date.now();
  }

  async function handleSaveChanges() {
    if (!modalMatch || !tournament || !canEditScores) { closeModal(); return; }
    try {
      setIsSaving(true);

      // Helper to get all names in bracket to prevent duplicates
      const getAllPlayerNames = (bracket) => {
        const names = new Set();
        const add = (p) => {
          if (p && p !== "(TBD)" && p !== "TBD" && p !== "(Bye)" && p !== "No Opponent") names.add(p);
        };
        (bracket.rounds || []).forEach(r => r.forEach(m => {
          add(m.player1);
          add(m.player2);
        }));
        return names;
      };

      const allNames = getAllPlayerNames(tournament.bracket);
      let p1 = String(editData.player1 || "").trim();
      let p2 = String(editData.player2 || "").trim();
      let warningMsg = "";

      const uniquify = (name, existing) => {
        let newName = name;
        while (existing.has(newName)) {
          const match = newName.match(/^(.*?) \[(\d+)\]$/);
          if (match) {
            newName = `${match[1]} [${parseInt(match[2], 10) + 1}]`;
          } else {
            newName = `${newName} [1]`;
          }
        }
        return newName;
      };

      // Check Player 1
      const reserved = ["(Bye)", "(TBD)", "No Opponent", "TBD"];
      if (reserved.includes(p1)) {
        alert(`Name "${p1}" is reserved.`);
        return;
      }

      if (p1 && p1 !== "(TBD)" && p1 !== "TBD" && p1 !== "(Bye)" && p1 !== "No Opponent" && p1 !== modalMatch.player1 && allNames.has(p1)) {
        const newName = uniquify(p1, allNames);
        warningMsg += `Player "${p1}" already exists. Renamed to "${newName}".\n`;
        p1 = newName;
      }

      // Check Player 2 (include p1 in the check set to avoid collision if p1 was just renamed to what p2 wants)
      const currentNames = new Set(allNames);
      if (p1) currentNames.add(p1);

      if (reserved.includes(p2)) {
        alert(`Name "${p2}" is reserved.`);
        return;
      }

      if (p2 && p2 !== "(TBD)" && p2 !== "TBD" && p2 !== "(Bye)" && p2 !== "No Opponent" && p2 !== modalMatch.player2 && currentNames.has(p2)) {
        const newName = uniquify(p2, currentNames);
        warningMsg += `Player "${p2}" already exists. Renamed to "${newName}".\n`;
        p2 = newName;
      }

      if (warningMsg) alert(warningMsg);

      // Optimistic update — apply immediately so UI responds instantly
      const newData = {
        player1: p1,
        score1: editData.score1 === "" ? 0 : Number(editData.score1),
        player2: p2,
        score2: editData.score2 === "" ? 0 : Number(editData.score2)
      };
      const updated = JSON.parse(JSON.stringify(tournament));

      const match = updated.bracket.rounds[modalMatch.roundIndex][modalMatch.matchIndex];
      Object.assign(match, newData);

      // Propagate changes locally before saving to ensure "filling in" happens instantly
      resolveAllByes(updated.bracket, tournament.type, true);

      // Close modal and apply optimistic state immediately — no flicker
      setModalVisible(false);
      setModalMatch(null);
      setClosingModal(false);
      setTournament(updated);

      // Persist to server, then silently sync server state (winner propagation etc.)
      await axios.put(
        `/api/tournaments/${tournament._id}`,
        { bracket: updated.bracket, matchId: modalMatch.id, editData },
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      refreshSilently(); // no loading state, just syncs server-propagated changes quietly
    } catch (err) {
      console.error("Error saving changes:", err);
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading || !tournament) {
    return (
      <div className="zft-page">
        <div className="zft-bg" /><div className="zft-grid" /><div className="zft-blob" /><div className="zft-blob2" />
        <div className="td-wrap">
          <h1 className="td-title">Loading…</h1>
          <div className="bracket-card"><div className="canvas" /></div>
        </div>
      </div>
    );
  }

  const standings = tournament.started ? computeStandings(tournament) : [];
  const top3 = standings.slice(0, 3);
  const fullList = standings; // all players including top 3, shown in full standings

  // Helpers: get locked/reversible round indices for a specific bracket key
  function getIndices(arr, key) {
    if (!arr?.length) return [];
    const prefix = key + "_";
    return arr.filter(k => k.startsWith(prefix)).map(k => parseInt(k.slice(prefix.length), 10));
  }
  const lockedSingle    = getIndices(tournament.lockedRounds, "single");
  const reversibleSingle = getIndices(tournament.reversibleRounds, "single");

  const bracketProps = {
    onMatchClick: handleMatchClick,
    modalVisible,
    closingModal,
    modalMatch,
    canEditScores,
    canEditNamesBase: canEditNamesGlobal,
    modalMatchLocked,
    tournamentStarted: tournament.started,
    editData,
    setEditData,
    onSave: handleSaveChanges,
    onClose: closeModal,
    modalOpenedAt: modalOpenedAtRef.current,
    isSaving,
  };

  return (
    <div className="zft-page">
      <div className="zft-bg" />
      <div className="zft-grid" />
      <div className="zft-blob" />
      <div className="zft-blob2" />

      <div className="td-wrap">
        <h1 className="td-title">{tournament.name}</h1>

        {/* ── Top 3 always visible below title ── */}
        {tournament.started && top3.length > 0 && (
          <div className="top3-bar">
            {top3.map((p, i) => (
              <div key={p.name} className={`top3-item ${MEDAL_CLASSES[i]}`}>
                <span className="top3-medal">{MEDALS[i]}</span>
                <span className="top3-name">{p.name}</span>
                <span className="top3-record">{p.wins}W – {p.losses}L</span>
              </div>
            ))}
          </div>
        )}

        {/* Brackets */}
        {tournament.type === "single" && singleLayout && (
          <div className="bracket-card">
            {canLockRounds && tournament.started && (
              <RoundLockBar roundCount={singleLayout.roundLayouts.length} lockedIndices={lockedSingle} reversibleIndices={reversibleSingle} bracketKey="single" onLock={lockRound} onUnlock={unlockRound} canUnlock={canUnlockRounds} />
            )}
            <FitPanZoomCanvas layout={singleLayout} {...bracketProps}
              lockedRoundIndices={lockedSingle}
              statusInfo={{ type: tournament.type, started: tournament.started, ended: tournament.ended }} />
          </div>
        )}

        {/* ── Full standings toggle (all players) ── */}
        {tournament.started && (
          <>
            <button
              className={`standings-toggle${showFullStandings ? " open" : ""}`}
              onClick={() => setShowFullStandings((v) => !v)}
            >
              {showFullStandings ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              <span>{showFullStandings ? "Hide Full Standings" : "View Full Standings"}</span>
            </button>

            {showFullStandings && (
              <div className="standings-card">
                <h2 className="standings-title">Full Standings</h2>

                {fullList.length === 0 ? (
                  <p className="standings-empty">No matches played yet.</p>
                ) : (
                  <div className="standings-list">
                    {fullList.map((p, i) => (
                      <div key={p.name} className={`standings-row${i < 3 ? " standings-top" : ""}`}>
                        <span className="standings-rank">
                          {i < 3 ? MEDALS[i] : `#${i + 1}`}
                        </span>
                        <span className="standings-name">{p.name}</span>
                        <span className="standings-record">{p.wins}W – {p.losses}L</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Match details modal */}
      {modalVisible && (
        <div
          ref={modalOverlayRef}
          className={`modal-overlay ${closingModal ? "closing" : "open"}`}
          onClick={handleOverlayClick}
        >
          <div
            className={`modal ${closingModal ? "closing" : "open"}`}
            style={{ width: 420, maxWidth: "92vw" }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalContent
              modalMatch={modalMatch}
              canEditScores={canEditScores}
              canEditNamesBase={canEditNamesGlobal}
              matchLocked={modalMatchLocked}
              tournamentStarted={tournament.started}
              editData={editData}
              setEditData={setEditData}
              onSave={handleSaveChanges}
              isSaving={isSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Round Lock Bar ===== */
function RoundLockBar({ roundCount, lockedIndices, reversibleIndices = [], bracketKey, onLock, onUnlock, canUnlock = false }) {
  const [confirmingIdx, setConfirmingIdx] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [confirmError, setConfirmError] = useState("");

  if (roundCount === 0) return null;

  function startConfirm(i) { setConfirmingIdx(i); setConfirmText(""); setConfirmError(""); }
  function cancelConfirm()  { setConfirmingIdx(null); setConfirmText(""); setConfirmError(""); }
  function submitConfirm() {
    const expected = `lock round ${confirmingIdx + 1}`;
    if (confirmText.trim().toLowerCase() !== expected) {
      setConfirmError(`Type exactly "lock round ${confirmingIdx + 1}"`);
      return;
    }
    const idx = confirmingIdx;
    cancelConfirm();
    onLock(bracketKey, idx);
  }

  return (
    <>
      <div className="round-lock-bar">
        {Array.from({ length: roundCount }, (_, i) => {
          const locked = lockedIndices.includes(i);
          const reversible = reversibleIndices.includes(i);
          const prevRoundLocked = i === 0 || lockedIndices.includes(i - 1);
          const nextRoundLocked = lockedIndices.includes(i + 1);

          if (locked) {
            return (
              <div key={i} className="round-lock-locked-group">
                <span className="round-lock-btn locked">🔒 Round {i + 1} Locked</span>
                {reversible && onUnlock && canUnlock && !nextRoundLocked && (
                  <button className="round-lock-btn unlock-btn" onClick={() => onUnlock(bracketKey, i)}>Unlock</button>
                )}
              </div>
            );
          }
          return (
            <button
              key={i}
              className="round-lock-btn"
              onClick={() => startConfirm(i)}
              disabled={!prevRoundLocked}
              style={!prevRoundLocked ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              title={!prevRoundLocked ? "Previous round must be locked first" : ""}
            >
              Lock Round {i + 1}
            </button>
          );
        })}
      </div>

      {confirmingIdx !== null && (
        <div className="modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) cancelConfirm(); }}>
          <div className="modal open" style={{ width: 420, maxWidth: "92vw" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <h3>Lock Round {confirmingIdx + 1}</h3>
              <p className="lock-confirm-desc">
                Unplayed matches will be coin-flipped. If any matches had real scores this cannot be undone.
              </p>
              <label style={{ display: "grid", gap: ".5rem" }}>
                <span className="lock-confirm-prompt">
                  Type <strong className="lock-confirm-keyword">lock round {confirmingIdx + 1}</strong> to confirm:
                </span>
                <input
                  type="text"
                  value={confirmText}
                  autoFocus
                  placeholder={`lock round ${confirmingIdx + 1}`}
                  onChange={e => { setConfirmText(e.target.value); setConfirmError(""); }}
                  onKeyDown={e => { if (e.key === "Enter") submitConfirm(); if (e.key === "Escape") cancelConfirm(); }}
                />
                {confirmError && <span className="lock-confirm-error">{confirmError}</span>}
              </label>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button className="button" style={{ flex: 1 }} onClick={submitConfirm}>Confirm Lock</button>
                <button className="btn" style={{ flex: 1 }} onClick={cancelConfirm}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ===== Modal body ===== */

// Splits "PlayerName (discordId)" into { base, discord }
// If no parenthetical, returns { base: fullName, discord: null }
function formatPlayerName(fullName) {
  if (!fullName) return { base: "", discord: null };
  const m = fullName.match(/^(.*?)\s+\(([^)]+)\)$/);
  if (m) return { base: m[1], discord: m[2] };
  return { base: fullName, discord: null };
}

// Renders a player name with the base truncated via CSS and the Discord ID kept intact
function PlayerNameDisplay({ name }) {
  const { base, discord } = formatPlayerName(name);
  return (
    <span className="modal-player-name">
      <span className="modal-player-base">{base}</span>
      {discord && <span className="modal-player-discord">({discord})</span>}
    </span>
  );
}

// Names are only editable in the very first round of each bracket section.
// Rounds 1+ have propagated names that should never be changed manually.
function isFirstRound(matchId) {
  if (!matchId) return false;
  if (matchId.startsWith("L-") || matchId.startsWith("F-")) return false;
  if (matchId.startsWith("W-")) return matchId.startsWith("W-R0-");
  return matchId.startsWith("R0-");
}

function ModalContent({ modalMatch, canEditScores, canEditNamesBase, matchLocked, tournamentStarted, editData, setEditData, onSave, isSaving }) {
  if (!modalMatch) return null;
  // matchLocked overrides editing even for admins — round is officially closed
  const canEdit = canEditScores && tournamentStarted && !matchLocked;
  // Staff (canEditNamesBase=false) can NEVER edit names; also round 1+ names are locked
  const canEditNames = canEdit && !!canEditNamesBase && isFirstRound(modalMatch.id);

  return (
    <div className="modal-body">
      <h3>Match Details</h3>

      {!canEdit ? (
        <div className="match-view">
          <div className="match-view-row">
            <strong className="match-view-player"><PlayerNameDisplay name={modalMatch.player1} /></strong>
            <span className="match-view-score">{modalMatch.score1}</span>
          </div>
          <div className="match-view-row">
            <strong className="match-view-player"><PlayerNameDisplay name={modalMatch.player2} /></strong>
            <span className="match-view-score">{modalMatch.score2}</span>
          </div>
        </div>
      ) : (
        <>
          <label style={{ display: "grid", gap: ".5rem", minWidth: 0 }}>
            Player 1
            {canEditNames
              ? <input type="text" value={editData.player1} onChange={(e) => setEditData({ ...editData, player1: e.target.value })} />
              : <span className="modal-player-locked"><PlayerNameDisplay name={editData.player1} /></span>
            }
          </label>
          <label style={{ display: "grid", gap: ".5rem" }}>
            Score 1
            <input type="number" value={editData.score1} onChange={(e) => setEditData({ ...editData, score1: e.target.value })} />
          </label>
          <label style={{ display: "grid", gap: ".5rem", minWidth: 0 }}>
            Player 2
            {canEditNames
              ? <input type="text" value={editData.player2} onChange={(e) => setEditData({ ...editData, player2: e.target.value })} />
              : <span className="modal-player-locked"><PlayerNameDisplay name={editData.player2} /></span>
            }
          </label>
          <label style={{ display: "grid", gap: ".5rem" }}>
            Score 2
            <input type="number" value={editData.score2} onChange={(e) => setEditData({ ...editData, score2: e.target.value })} />
          </label>
          <button className="button" onClick={onSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </>
      )}
    </div>
  );
}

/* ===========================
   Pan/Zoom Canvas
   =========================== */
const FitPanZoomCanvas = React.memo(function FitPanZoomCanvas({
  layout, onMatchClick,
  modalVisible, closingModal, modalMatch,
  canEditScores, canEditNamesBase = false, modalMatchLocked = false, tournamentStarted,
  editData, setEditData, onSave, onClose, modalOpenedAt, isSaving,
  statusInfo = null,
  lockedRoundIndices = [],
}) {
  const { roundLayouts, lines, minX, maxX, minY, maxY, matchWidth = 248, matchHeight = 88 } = layout;

  const padding = 320;
  const contentWidth  = Math.max(1, maxX - minX) + padding;
  const contentHeight = Math.max(1, maxY - minY) + padding;

  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [transitionMs, setTransitionMs] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 640px)");
    const apply = () => setIsSmallScreen(!!mq?.matches);
    apply();
    mq?.addEventListener?.("change", apply);
    return () => mq?.removeEventListener?.("change", apply);
  }, []);

  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const mouseDownPosRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);
  const DRAG_CLICK_THRESHOLD = 5;
  const [query, setQuery] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [searchCount, setSearchCount] = useState({ current: 0, total: 0 });
  const searchIdxRef = useRef(0);
  const searchQueryRef = useRef("");
  const searchInputRef = useRef(null);
  const fsSearchRef = useRef(null);
  const pinchInitialDistanceRef = useRef(null);
  const pinchInitialScaleRef = useRef(1);
  const pinchWorldPointRef = useRef({ wx: 0, wy: 0 });
  const singleTouchStartRef = useRef({ x: 0, y: 0 });

  const getLocalPoint = (clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const screenToWorld = (sx, sy) => ({ wx: (sx - offset.x) / scale, wy: (sy - offset.y) / scale });

  const allMatches = useMemo(() => {
    const arr = [];
    roundLayouts.forEach((rnd) => rnd.forEach((m) => { if (m) arr.push(m); }));
    return arr;
  }, [roundLayouts]);

  const isInteractiveTarget = (el) => {
    if (!el) return false;
    const t = el.tagName;
    if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || t === "BUTTON") return true;
    if (el.closest(".ui-block")) return true;
    return false;
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        if (isFullscreen) fsSearchRef.current?.focus();
        else searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () => document.removeEventListener("keydown", onKey, { capture: true });
  }, [isFullscreen]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const block = (e) => e.preventDefault();
    el.addEventListener("wheel", block, { passive: false });
    return () => el.removeEventListener("wheel", block);
  }, []);

  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(!!fsEl);
    };
    ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"]
      .forEach((ev) => document.addEventListener(ev, onChange));
    return () => ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"]
      .forEach((ev) => document.removeEventListener(ev, onChange));
  }, []);

  const requestFS = () => {
    const el = containerRef.current;
    if (!el) return;
    (el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen)?.call(el);
  };
  const exitFS = () => {
    (document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen)?.call(document);
  };

  const setTransform = useCallback((nextScale, nextOffset, ms = 0) => {
    setTransitionMs(ms);
    setScale(nextScale);
    setOffset(nextOffset);
    if (ms > 0) {
      window.clearTimeout(setTransform._t);
      setTransform._t = window.setTimeout(() => setTransitionMs(0), ms + 30);
    }
  }, []);
  setTransform._t = setTransform._t;

  const fitToView = useCallback((ms = 420) => {
    const el = containerRef.current;
    if (!el) return;
    const pad = 24;
    const sx = (el.clientWidth - pad) / contentWidth;
    const sy = (el.clientHeight - pad) / contentHeight;
    const s = Math.min(Math.max(0.2, Math.min(sx, sy)), 1.2);
    setTransform(s, { x: (el.clientWidth - contentWidth * s) / 2, y: (el.clientHeight - contentHeight * s) / 2 }, ms);
  }, [contentWidth, contentHeight, setTransform]);

  const resetView = useCallback((ms = 420) => setTransform(1, { x: 0, y: 0 }, ms), [setTransform]);

  const zoomAtPoint = useCallback((cx, cy, nextScale, ms = 120) => {
    const clamped = Math.max(0.2, Math.min(nextScale, 5));
    const k = clamped / scale;
    setTransform(clamped, { x: cx - (cx - offset.x) * k, y: cy - (cy - offset.y) * k }, ms);
  }, [scale, offset.x, offset.y, setTransform]);

  function handleMouseDown(e) {
    if (isInteractiveTarget(e.target)) return;
    e.preventDefault();
    setDragging(true);
    setTransitionMs(0);
    const { x, y } = getLocalPoint(e.clientX, e.clientY);
    dragStart.current = { x: x - offset.x, y: y - offset.y };
    mouseDownPosRef.current = { x, y };
    dragMovedRef.current = false;
  }
  function handleMouseMove(e) {
    if (!dragging) return;
    e.preventDefault();
    const { x, y } = getLocalPoint(e.clientX, e.clientY);
    setOffset({ x: x - dragStart.current.x, y: y - dragStart.current.y });
    if (!dragMovedRef.current && Math.hypot(x - mouseDownPosRef.current.x, y - mouseDownPosRef.current.y) > DRAG_CLICK_THRESHOLD) {
      dragMovedRef.current = true;
    }
  }
  function handleMouseUp() { setDragging(false); }
  function handleMouseLeave() { setDragging(false); }

  function handleWheel(e) {
    const { x: cx, y: cy } = getLocalPoint(e.clientX, e.clientY);
    if (e.shiftKey) { setTransform(scale, { x: offset.x - e.deltaY, y: offset.y }, 80); return; }
    zoomAtPoint(cx, cy, scale * (e.deltaY < 0 ? 1.12 : 0.9), 160);
  }

  function handleTouchStart(e) {
    if (e.touches.length === 1) {
      const { x, y } = getLocalPoint(e.touches[0].clientX, e.touches[0].clientY);
      setTransitionMs(0); setDragging(true);
      singleTouchStartRef.current = { x, y };
      dragStart.current = { x: x - offset.x, y: y - offset.y };
      dragMovedRef.current = false;
    } else if (e.touches.length === 2) {
      const p0 = getLocalPoint(e.touches[0].clientX, e.touches[0].clientY);
      const p1 = getLocalPoint(e.touches[1].clientX, e.touches[1].clientY);
      const cx = (p0.x + p1.x) / 2, cy = (p0.y + p1.y) / 2;
      pinchInitialDistanceRef.current = Math.hypot(p0.x - p1.x, p0.y - p1.y);
      pinchInitialScaleRef.current = scale;
      pinchWorldPointRef.current = screenToWorld(cx, cy);
      dragMovedRef.current = false;
    }
  }
  function handleTouchMove(e) {
    if (e.touches.length === 1 && dragging) {
      const { x, y } = getLocalPoint(e.touches[0].clientX, e.touches[0].clientY);
      setOffset({ x: x - dragStart.current.x, y: y - dragStart.current.y });
      if (Math.hypot(x - singleTouchStartRef.current.x, y - singleTouchStartRef.current.y) > DRAG_CLICK_THRESHOLD) dragMovedRef.current = true;
    } else if (e.touches.length === 2 && pinchInitialDistanceRef.current) {
      const p0 = getLocalPoint(e.touches[0].clientX, e.touches[0].clientY);
      const p1 = getLocalPoint(e.touches[1].clientX, e.touches[1].clientY);
      const cx = (p0.x + p1.x) / 2, cy = (p0.y + p1.y) / 2;
      const dist = Math.hypot(p0.x - p1.x, p0.y - p1.y);
      const nextScale = Math.max(0.2, Math.min(5, (dist / pinchInitialDistanceRef.current) * pinchInitialScaleRef.current));
      const { wx, wy } = pinchWorldPointRef.current;
      setScale(nextScale);
      setOffset({ x: cx - wx * nextScale, y: cy - wy * nextScale });
      dragMovedRef.current = true;
    }
  }
  function handleTouchEnd(e) {
    if (e.touches.length === 0) { setDragging(false); pinchInitialDistanceRef.current = null; }
    else if (e.touches.length === 1) {
      const { x, y } = getLocalPoint(e.touches[0].clientX, e.touches[0].clientY);
      setDragging(true);
      dragStart.current = { x: x - offset.x, y: y - offset.y };
      singleTouchStartRef.current = { x, y };
    }
  }

  function handleSearch() {
    const q = query.trim().toLowerCase();
    if (!q) { setHighlightId(null); setSearchCount({ current: 0, total: 0 }); return; }
    const results = allMatches.filter((m) => m.player1?.toLowerCase().includes(q) || m.player2?.toLowerCase().includes(q));
    if (results.length === 0) { setHighlightId(null); setSearchCount({ current: 0, total: 0 }); return; }
    // Reset cycle index when the query text changes
    if (q !== searchQueryRef.current) { searchIdxRef.current = 0; searchQueryRef.current = q; }
    const idx = searchIdxRef.current % results.length;
    const found = results[idx];
    searchIdxRef.current++;
    setSearchCount({ current: idx + 1, total: results.length });
    setHighlightId(found.id);
    const el = containerRef.current;
    if (!el) return;
    const { width: cw, height: ch } = el.getBoundingClientRect();
    const wx = (found.x - minX + padding / 2 - 20) + matchWidth / 2;
    const wy = (found.y - minY + padding / 2 - 20) + matchHeight / 2;
    setTransform(scale, { x: cw / 2 - wx * scale, y: ch / 2 - wy * scale }, 260);
  }

  const bw = Math.max(1, contentWidth);
  const bh = Math.max(1, contentHeight);

  return (
    <>
      {!isFullscreen && (
        <div className="controls-wrap ui-block" style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
          <div className="controls ui-block" style={{ marginBottom: 0, display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <div className="search ui-block">
              <input ref={searchInputRef} type="text" placeholder="Search player…" value={query}
                onChange={(e) => { setQuery(e.target.value); searchQueryRef.current = ""; setSearchCount({ current: 0, total: 0 }); }}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }} />
              <button className="btn ui-block" onClick={handleSearch}>Find</button>
              {searchCount.total > 0 && (
                <span className="search-count ui-block">{searchCount.current}/{searchCount.total}</span>
              )}
            </div>
            <button className="btn btn-primary ui-block" onClick={() => fitToView(420)}>Fit to view</button>
            <button className="btn ui-block" onClick={() => resetView(420)} title="Reset pan/zoom">
              <FaRedo style={{ verticalAlign: "-2px" }} /> Reset
            </button>
          </div>
          {statusInfo && (
            <div className="controls-status ui-block" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <span className="type-pill controls-pill">
                Single Elim
              </span>
              {statusInfo.ended && <span className="status-badge ended controls-pill">Ended</span>}
              {!statusInfo.ended && statusInfo.started && <span className="status-badge in-progress controls-pill">In Progress</span>}
              {!statusInfo.started && <span className="status-badge open controls-pill">Open</span>}
            </div>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className="canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {!isFullscreen && !isSmallScreen && (
          <button className="fs-enter ui-block" onClick={requestFS} aria-label="Enter fullscreen">⛶</button>
        )}

        {isFullscreen && (
          <>
            <button className="fs-exit ui-block" onClick={exitFS} aria-label="Exit fullscreen">✕</button>
            <div className="fs-tools ui-block">
              <input ref={fsSearchRef} type="text" placeholder="Search player…" value={query}
                onChange={(e) => { setQuery(e.target.value); searchQueryRef.current = ""; setSearchCount({ current: 0, total: 0 }); }}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }} />
              <button className="btn ui-block" onClick={handleSearch}>Find</button>
              {searchCount.total > 0 && (
                <span className="search-count ui-block">{searchCount.current}/{searchCount.total}</span>
              )}
              <button className="btn btn-primary ui-block" onClick={() => fitToView(420)}>Fit</button>
              <button className="btn ui-block" onClick={() => resetView(420)}><FaRedo style={{ verticalAlign: "-2px" }} /></button>
            </div>
          </>
        )}

        <div className="zoom-stack ui-block">
          <button className="zoom-btn ui-block" onClick={() => {
            const rect = containerRef.current?.getBoundingClientRect();
            zoomAtPoint(rect ? rect.width / 2 : 0, rect ? rect.height / 2 : 0, scale * 1.12, 180);
          }}>+</button>
          <button className="zoom-btn ui-block" onClick={() => {
            const rect = containerRef.current?.getBoundingClientRect();
            zoomAtPoint(rect ? rect.width / 2 : 0, rect ? rect.height / 2 : 0, scale * 0.9, 180);
          }}>–</button>
        </div>

        {/* Scene */}
        <div style={{
          width: bw, height: bh,
          transform: `translate(${offset.x}px,${offset.y}px) scale(${scale})`,
          transformOrigin: "0 0", position: "absolute", left: 0, top: 0,
          transition: transitionMs ? `transform ${transitionMs}ms cubic-bezier(.2,.8,.2,1)` : "none"
        }}>
          <svg style={{ position: "absolute", left: 0, top: 0, width: bw, height: bh, overflow: "visible", zIndex: 0, pointerEvents: "none" }}>
            <defs>
              <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   style={{ stopColor: "var(--accent-gold,#d4af37)", stopOpacity: 0.3 }} />
                <stop offset="50%"  style={{ stopColor: "var(--accent-gold-light,#f0d478)", stopOpacity: 0.5 }} />
                <stop offset="100%" style={{ stopColor: "var(--accent-gold,#d4af37)", stopOpacity: 0.3 }} />
              </linearGradient>
            </defs>
            {lines.map((ln, i) => {
              const x1 = ln.x1 - minX + padding / 2 - 20, y1 = ln.y1 - minY + padding / 2 - 20;
              const x2 = ln.x2 - minX + padding / 2 - 20, y2 = ln.y2 - minY + padding / 2 - 20;
              const mx = (x1 + x2) / 2;
              return <path key={i} d={`M ${x1} ${y1} Q ${mx} ${y1} ${mx} ${(y1+y2)/2} T ${x2} ${y2}`} className="path" />;
            })}
          </svg>

          {roundLayouts.map((roundMatches, rIndex) => (
            <React.Fragment key={`round-${rIndex}`}>
              {roundMatches.map((match) => {
                if (!match) return null;
                const bigger = Math.max(match.score1, match.score2);
                const boxX = match.x - minX + padding / 2 - 20;
                const boxY = match.y - minY + padding / 2 - 20;
                return (
                  <div
                    key={match.id}
                    className={`match${highlightId === match.id ? " highlight" : ""}`}
                    style={{ left: boxX, top: boxY }}
                    onClick={() => { if (!dragMovedRef.current) onMatchClick?.(match); }}
                  >
                    <div className="row" style={{ marginBottom: 6 }}>
                      <span className="name">{match.player1 === "No Opponent" ? "No Opponent" : (match.player1 || "TBD")}</span>
                      <span className={`score${match.score1 === bigger && bigger !== 0 ? " win" : ""}`}>{match.score1}</span>
                    </div>
                    <div className="row">
                      <span className="name">{match.player2 === "No Opponent" ? "No Opponent" : (match.player2 || "TBD")}</span>
                      <span className={`score${match.score2 === bigger && bigger !== 0 ? " win" : ""}`}>{match.score2}</span>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Fullscreen modal */}
        {isFullscreen && modalVisible && (
          <div
            className={`modal-overlay ${closingModal ? "closing" : "open"}`}
            data-role="fs-backdrop"
            onClick={(e) => {
              if (e.target.dataset.role === "fs-backdrop" && Date.now() - (modalOpenedAt || 0) >= 220) onClose?.();
            }}
            style={{ position: "absolute" }}
          >
            <div className={`modal ${closingModal ? "closing" : "open"}`} style={{ width: 420, maxWidth: "92vw" }} onClick={(e) => e.stopPropagation()}>
              <ModalContent modalMatch={modalMatch} canEditScores={canEditScores} canEditNamesBase={canEditNamesBase} matchLocked={modalMatchLocked} tournamentStarted={tournamentStarted} editData={editData} setEditData={setEditData} onSave={onSave} isSaving={isSaving} />
            </div>
          </div>
        )}
      </div>
    </>
  );
});

export default TournamentDetail;
